import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { capturePostHogEvent } from '../../utils/analytics';
import {
  buildHostedCheckoutNavigation,
  completeHostedCheckoutReturn,
} from '../../utils/hostedCheckout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const MAX_PROCESSING_POLLS = 10;

export default function HostedCheckoutReturnPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleCustomJwtAuth } = useUnifiedAuth();
  const startedRef = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const params = new URLSearchParams(location.search);
    const attemptId = params.get('attempt');
    const returnToken = params.get('state');
    const sessionId = params.get('session_id');
    const canceled = params.get('canceled') === 'true';
    if (!attemptId || !returnToken || (!canceled && !sessionId)) {
      setError('Checkout return link is incomplete.');
      return;
    }

    let active = true;
    const finish = async () => {
      for (let poll = 0; poll < MAX_PROCESSING_POLLS && active; poll += 1) {
        const result = await completeHostedCheckoutReturn({
          apiUrl: API_URL,
          attemptId,
          returnToken,
          sessionId,
          canceled,
        });
        if (result.status === 'canceled') {
          capturePostHogEvent('checkout_experience_cancelled', { checkout_attempt_id: attemptId });
          navigate(result.cancelPath || '/pricing?checkoutCanceled=true', { replace: true });
          return;
        }
        if (result.status === 'processing') {
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
          continue;
        }
        if (result.token && handleCustomJwtAuth) {
          await handleCustomJwtAuth(result.token);
        }
        const destination = buildHostedCheckoutNavigation({
          redirectTo: result.redirectTo,
          returnPath: result.returnPath,
          userEmail: result.userEmail,
          origin: window.location.origin,
        });
        navigate(destination.path, {
          replace: true,
          state: destination.state,
        });
        return;
      }
      throw new Error('Payment is still processing. Check your email or account in a few minutes.');
    };
    void finish().catch((returnError) => {
      if (active)
        setError(
          returnError instanceof Error ? returnError.message : 'Checkout could not be verified.',
        );
    });
    return () => {
      active = false;
    };
  }, [handleCustomJwtAuth, location.search, navigate]);

  return (
    <main className="checkout-page" aria-live="polite">
      <div className="checkout-loading">
        {error ? (
          <>
            <h1>Payment needs review</h1>
            <p role="alert">{error}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/account-settings/subscriptions')}
            >
              View account
            </button>
          </>
        ) : (
          <>
            <Spinner animation="border" />
            <span>Verifying payment and opening your account…</span>
          </>
        )}
      </div>
    </main>
  );
}
